/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';

import { urlFiltersToCioFacets } from '~/grid/utils/cioUtils';

import useCioBrowseAndGqlProducts from './useCioBrowseAndGqlProducts';

const SelectedFacetsToApplyContext = React.createContext();

const useSelectedFacetsToApply = () => {
  const context = React.useContext(SelectedFacetsToApplyContext);

  if (!context) {
    throw new Error(
      'useSelectedFacetsToApply must be used within a SelectedFacetsToApplyProvider'
    );
  }

  return context;
};

const SelectedFacetsToApplyProvider = ({ children }) => {
  const [selectedFacetsToApply, setSelectedFacetsToApply] = useState({});
  const { selectedUrlFilters } = useCioBrowseAndGqlProducts();

  // Parse the url params and save them in the state
  // This state needs to be managed so that the user can select multiple facets
  // before applying them and updating the url all at the same time
  useEffect(() => {
    const newSelectedFacetsToApply = Object.entries(selectedUrlFilters).reduce(
      (acc, [facetName, facetOptions]) => {
        const cioFacetName = urlFiltersToCioFacets[facetName] || facetName;
        return { ...acc, [cioFacetName]: facetOptions };
      },
      {}
    );
    setSelectedFacetsToApply(newSelectedFacetsToApply);
  }, [selectedUrlFilters]);

  const toggleSelectedFacetsToApply = ({ facetName, facetOption }) => {
    setSelectedFacetsToApply((prev) => {
      if (!prev[facetName]) {
        return {
          ...prev,
          [facetName]: [facetOption],
        };
      }

      // If option is already selected, remove it
      // If option is not selected, add it
      const isFacetOptionSelected = prev[facetName].includes(facetOption);
      const newFacetOptions = isFacetOptionSelected
        ? prev[facetName].filter((option) => option !== facetOption)
        : [...prev[facetName], facetOption];

      const newSelectedFacetsToApply = {
        ...prev,
        [facetName]: newFacetOptions,
      };

      return newSelectedFacetsToApply;
    });
  };

  const isFacetOptionSelected = ({ facetName, facetOption }) => {
    return selectedFacetsToApply[facetName]?.includes(facetOption);
  };

  // we need to set the selectedFacetsToApply to an empty array
  // so that `getNewQueryParamString` can remove the query param from the URL
  const clearAllSelectedFacetsToApply = () => {
    const newSelectedFacetsToApply = Object.entries(
      selectedFacetsToApply
    ).reduce((acc, [facetName]) => {
      return { ...acc, [facetName]: [] };
    }, {});
    setSelectedFacetsToApply(newSelectedFacetsToApply);
  };

  return (
    <SelectedFacetsToApplyContext.Provider
      value={{
        selectedFacetsToApply,
        setSelectedFacetsToApply,
        toggleSelectedFacetsToApply,
        isFacetOptionSelected,
        clearAllSelectedFacetsToApply,
      }}
    >
      {children}
    </SelectedFacetsToApplyContext.Provider>
  );
};

export default useSelectedFacetsToApply;
export { SelectedFacetsToApplyProvider };
